import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

/**
 * Custom hook to manage onboarding visibility and state
 * Centralizes all onboarding logic to avoid duplication across components
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.shouldCheck - Whether to check onboarding (e.g., after page loads)
 * @param {string} options.userId - User ID to check onboarding status for
 * @returns {[boolean, function]} [showOnboarding, setShowOnboarding] - State and setter for onboarding visibility
 */
export const useOnboarding = ({ shouldCheck = true, userId = null } = {}) => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!shouldCheck || !userId) return;

    const checkOnboarding = async () => {
      try {
        // Check if this is intentional navigation from onboarding (user clicked "Take me there")
        const isNavigating = localStorage.getItem('onboarding_navigating');
        if (isNavigating) {
          // Clear the navigating flag
          localStorage.removeItem('onboarding_navigating');
          // Clear exit flags if they exist (since this is intentional navigation)
          localStorage.removeItem('onboarding_exited');
          localStorage.removeItem('onboarding_exit_timestamp');
        }

        // Check if user has explicitly exited onboarding (closed, skipped, or navigated away manually)
        // But only if it wasn't intentional navigation
        const hasExited = localStorage.getItem('onboarding_exited');
        if (hasExited && !isNavigating) {
          // Check if exit was recent (within 1 hour) - if so, don't show again
          const exitTimestamp = localStorage.getItem('onboarding_exit_timestamp');
          if (exitTimestamp) {
            const exitTime = new Date(exitTimestamp);
            const now = new Date();
            const hoursDiff = (now - exitTime) / (1000 * 60 * 60);
            
            // If user exited more than 1 hour ago, mark onboarding as completed
            if (hoursDiff >= 1) {
              try {
                await supabase
                  .from('user_profiles')
                  .update({ onboarding_completed: true })
                  .eq('user_id', userId);
                
                // Clear exit flags
                localStorage.removeItem('onboarding_exited');
                localStorage.removeItem('onboarding_exit_timestamp');
                localStorage.removeItem('onboarding_progress');
              } catch (e) {
                console.error('Error marking onboarding as complete:', e);
              }
            } else {
              // User exited recently, don't show onboarding again
              return;
            }
          } else {
            // No timestamp, treat as exited and don't show
            return;
          }
        }
        
        const { data } = await supabase
          .from('user_profiles')
          .select('onboarding_completed')
          .eq('user_id', userId)
          .maybeSingle();
        
        if (data && !data.onboarding_completed) {
          // Check if there's saved progress from intentional navigation
          // If so, they can continue the tour
          const savedProgress = localStorage.getItem('onboarding_progress');
          if (savedProgress) {
            try {
              const progress = JSON.parse(savedProgress);
              const savedTime = new Date(progress.timestamp);
              const now = new Date();
              const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
              
              // If progress is less than 24 hours old, show onboarding
              if (hoursDiff < 24) {
                // Progress exists and is recent - show onboarding
                // If this was intentional navigation, show immediately
                const delay = isNavigating ? 300 : 500;
                setTimeout(() => {
                  setShowOnboarding(true);
                }, delay);
                return;
              }
            } catch (e) {
              // Invalid progress, ignore
            }
          }
          
          // Small delay to ensure page is fully rendered and user sees the content
          setTimeout(() => {
            setShowOnboarding(true);
          }, 800);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };
    
    checkOnboarding();
  }, [shouldCheck, userId]);

  return [showOnboarding, setShowOnboarding];
};
